/* Photo picker for the forms that take one photo (product registration, part request).
   The photo is shrunk in the browser first (long side 1600 px, JPEG), so phones do not upload 5 MB files.
   SmPhotoPicker(wrap, text) takes the element marked data-sm-photo and the two messages shown when a file cannot be used
   ({ tooLarge, unreadable }). It returns null when the markup is not on the page, otherwise:
     input      the file box
     get()      the shrunk photo as { data: base64 text, type: 'image/jpeg' }, or null
     pending()  true once a file has been chosen and not removed or refused
     ready()    a promise that settles when no photo is being shrunk any more
     clear(msg) removes the photo and shows msg (or nothing) under the box
   The element gets the event "sm-photo-refused" when a chosen file is refused (too large, or it cannot be opened)
   and "sm-photo-accepted" when a photo is ready.
   While the form around it carries data-sm-busy (it is being sent) the visitor cannot change or remove the photo. */
window.SmPhotoPicker = function (wrap, text) {
  var fileInput = wrap && wrap.querySelector('input[type=file]');
  if (!fileInput) return null;
  text = text || {};
  var MAX_SIDE = 1600, MAX_INPUT = 25 * 1024 * 1024, MAX_OUTPUT = 1200 * 1024;
  var photo = null, photoJob = null, thumbUrl = '';
  var photoStatus = wrap.querySelector('[data-sm-photo-status]');
  var chosen = wrap.querySelector('[data-sm-photo-chosen]');
  var pick = wrap.querySelector('.sm-photo__pick');
  var removeBtn = wrap.querySelector('[data-sm-photo-remove]');
  var locked = function () { return !!wrap.closest('[data-sm-busy]'); };
  var tell = function (what) { try { wrap.dispatchEvent(new CustomEvent('sm-photo-' + what)); } catch (e) {} };
  var refused = function () { tell('refused'); };

  var encode = function (img, side, quality) {
    return new Promise(function (resolve, reject) {
      var w = img.naturalWidth, h = img.naturalHeight;
      if (!w || !h) return reject(new Error('empty'));
      var k = Math.min(1, side / Math.max(w, h)), c = document.createElement('canvas');
      c.width = Math.max(16, Math.round(w * k)); c.height = Math.max(16, Math.round(h * k));
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, c.height);   // see-through PNGs get a white background
      ctx.drawImage(img, 0, 0, c.width, c.height);
      c.toBlob(function (blob) { if (blob && blob.type === 'image/jpeg') resolve(blob); else reject(new Error('encode')); }, 'image/jpeg', quality);
    });
  };
  var shrink = function (file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file), img = new Image();
      var slow = setTimeout(function () { img.onload = img.onerror = null; finish(reject, new Error('slow')); }, 30000);   // never leave a file "opening" for ever
      var finish = function (fn, arg) { clearTimeout(slow); URL.revokeObjectURL(url); fn(arg); };
      img.onload = function () {
        encode(img, MAX_SIDE, 0.82)
          .then(function (blob) { return blob.size > MAX_OUTPUT ? encode(img, 1280, 0.7) : blob; })
          .then(function (blob) { if (blob.size > MAX_OUTPUT) finish(reject, new Error('big')); else finish(resolve, blob); },
                function (err) { finish(reject, err); });
      };
      img.onerror = function () { finish(reject, new Error('decode')); };
      img.src = url;
    });
  };
  var toBase64 = function (blob) {
    return new Promise(function (resolve, reject) {
      var r = new FileReader();
      r.onload = function () { resolve(String(r.result).split(',')[1] || ''); };
      r.onerror = function () { reject(new Error('read')); };
      r.readAsDataURL(blob);
    });
  };
  var clear = function (message) {
    photo = null; photoJob = null;
    if (thumbUrl) { URL.revokeObjectURL(thumbUrl); thumbUrl = ''; }
    fileInput.value = ''; fileInput.hidden = false;
    chosen.hidden = true; pick.hidden = false;
    removeBtn.removeAttribute('aria-label');
    wrap.removeAttribute('data-busy');
    photoStatus.textContent = message || '';
  };
  fileInput.addEventListener('change', function () {
    var file = fileInput.files && fileInput.files[0];
    if (!file) { clear(''); return; }
    if (file.size > MAX_INPUT) { clear(text.tooLarge || 'That file is too large. Choose a smaller photo.'); refused(); return; }
    photo = null; photoStatus.textContent = ''; wrap.setAttribute('data-busy', '');
    var job = photoJob = shrink(file).then(function (blob) {
      return toBase64(blob).then(function (data) {
        if (job !== photoJob) return;   // another photo was chosen meanwhile
        photo = { data: data, type: 'image/jpeg' };
        if (thumbUrl) URL.revokeObjectURL(thumbUrl);
        thumbUrl = URL.createObjectURL(blob);
        chosen.querySelector('[data-sm-photo-thumb]').src = thumbUrl;
        chosen.querySelector('[data-sm-photo-name]').textContent = file.name || 'Photo added';
        removeBtn.setAttribute('aria-label', 'Remove photo' + (file.name ? ' ' + file.name : ''));   // focus lands here: this is how a screen reader hears the photo was taken
        var hadFocus = document.activeElement === fileInput;
        chosen.hidden = false; pick.hidden = true; fileInput.hidden = true;
        if (hadFocus) removeBtn.focus();   // the file box is hidden now: keep keyboard users in place
        wrap.removeAttribute('data-busy');
        tell('accepted');
      });
    }).catch(function () {
      if (job !== photoJob) return;
      clear(text.unreadable || 'We could not open that file. Choose a JPG or PNG photo, or take a screenshot and add that.');
      refused();
    });
  });
  removeBtn.addEventListener('click', function () { if (locked()) return; clear(''); fileInput.focus(); });
  fileInput.addEventListener('click', function (e) { if (locked()) e.preventDefault(); });
  /* A browser may put a file back in the box on reload or Back without telling anyone. Start empty, so the box shows the truth
     and the same photo can be chosen again. */
  fileInput.value = '';
  window.addEventListener('pageshow', function (e) { if (!e.persisted && !photoJob) fileInput.value = ''; });

  /* Waits for the photo being shrunk, and for a newer one chosen meanwhile. */
  var ready = function () { var j = photoJob; return Promise.resolve(j).then(function () { return photoJob && photoJob !== j ? ready() : null; }); };
  return { input: fileInput, get: function () { return photo; }, pending: function () { return !!photoJob; }, ready: ready, clear: clear };
};
